import React from 'react';

export function FermentationApproaches() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Four Basic Fermentation Approaches</h3>
        <p className="mb-4">
          The fermentation approach you choose significantly impacts your bread's flavor, texture, and the time required to make it. Each method has distinct advantages and characteristics that make it suitable for different baking scenarios.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">1. Direct Fermentation</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Overview</h4>
            <p className="text-sm text-white/80">
              Direct fermentation is the simplest approach, where all ingredients are mixed together at once, followed by a single bulk fermentation period before shaping and final proofing. This method is commonly used with commercial yeast but can also work with sourdough.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Characteristics</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Shortest total fermentation time</li>
                <li>Simplest process with fewest steps</li>
                <li>Less complex flavor development</li>
                <li>Good for everyday baking</li>
                <li>Typically 2-4 hours with commercial yeast</li>
                <li>6-8 hours with sourdough starter</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Best For</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Beginners learning bread basics</li>
                <li>Quick turnaround breads</li>
                <li>Sandwich loaves and everyday breads</li>
                <li>Situations where time is limited</li>
                <li>Enriched doughs (with eggs, butter, sugar)</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Process</h4>
            <ol className="list-decimal list-inside text-sm text-white/80 space-y-2">
              <li>Mix all ingredients (flour, water, salt, yeast/starter)</li>
              <li>Develop gluten through kneading or stretch-and-folds</li>
              <li>Bulk fermentation until doubled (2-4 hours for commercial yeast, 4-8 hours for sourdough)</li>
              <li>Shape the dough</li>
              <li>Final proof (1-2 hours for commercial yeast, 2-4 hours for sourdough)</li>
              <li>Bake</li>
            </ol>
          </div>
          
          <div className="bg-amber-500/10 p-4 rounded-lg">
            <h4 className="text-amber-400 font-medium mb-2">Pro Tips</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Even with direct fermentation, an autolyse (resting flour and water before adding salt and yeast) can improve dough quality</li>
              <li>For more flavor with commercial yeast, reduce yeast quantity and extend fermentation time</li>
              <li>Perform several sets of stretch and folds during bulk fermentation to develop strength</li>
              <li>Room temperature affects fermentation speed significantly—adjust times accordingly</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">2. Slow Fermentation (Cold Bulk)</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Overview</h4>
            <p className="text-sm text-white/80">
              Cold bulk fermentation involves mixing the dough and then refrigerating it for an extended period during the bulk fermentation phase. This slows down yeast activity while allowing enzyme activity and flavor development to continue.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Characteristics</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Extended fermentation (12-72 hours)</li>
                <li>Enhanced flavor complexity</li>
                <li>Improved digestibility</li>
                <li>Better gluten development</li>
                <li>More flexibility in baking schedule</li>
                <li>Works with both commercial yeast and sourdough</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Best For</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Artisan breads with complex flavor</li>
                <li>Bakers with busy schedules</li>
                <li>Planning ahead for weekend baking</li>
                <li>Doughs with higher hydration</li>
                <li>Breads that benefit from extended enzymatic activity</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Process</h4>
            <ol className="list-decimal list-inside text-sm text-white/80 space-y-2">
              <li>Mix all ingredients (use less yeast than direct method)</li>
              <li>Develop initial gluten structure (kneading or several sets of stretch-and-folds)</li>
              <li>Allow a short room temperature rest (1-2 hours)</li>
              <li>Refrigerate for bulk fermentation (12-72 hours)</li>
              <li>Remove from refrigerator and allow to warm slightly (1-2 hours)</li>
              <li>Shape the cold dough</li>
              <li>Final proof (2-4 hours, longer than direct method due to cold dough)</li>
              <li>Bake</li>
            </ol>
          </div>
          
          <div className="bg-amber-500/10 p-4 rounded-lg">
            <h4 className="text-amber-400 font-medium mb-2">Pro Tips</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Reduce yeast/starter quantity for longer cold fermentation periods</li>
              <li>Cold dough is easier to handle and shape, especially at higher hydration</li>
              <li>Cover dough well in refrigerator to prevent drying</li>
              <li>Allow sufficient time for final proof as cold dough takes longer to rise</li>
              <li>Cold fermentation develops more acetic acid, creating a more complex flavor profile</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">3. Slow Fermentation (Room Temperature)</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Overview</h4>
            <p className="text-sm text-white/80">
              Room temperature slow fermentation uses very small amounts of yeast or starter to extend the fermentation process at ambient temperature. This approach balances flavor development with practical considerations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Characteristics</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Extended fermentation (8-24 hours)</li>
                <li>Uses minimal yeast/starter (0.1-0.5%)</li>
                <li>Develops complex flavors</li>
                <li>No refrigeration required</li>
                <li>More lactic acid production (milder tang)</li>
                <li>Works with both commercial yeast and sourdough</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Best For</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Overnight fermentation</li>
                <li>Bakers without refrigerator space</li>
                <li>Traditional European bread styles</li>
                <li>Developing flavor without refrigeration</li>
                <li>Stable, cool room environments</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Process</h4>
            <ol className="list-decimal list-inside text-sm text-white/80 space-y-2">
              <li>Mix all ingredients with minimal yeast/starter (0.1-0.5% of flour weight)</li>
              <li>Develop initial gluten structure</li>
              <li>Bulk fermentation at room temperature (8-16 hours)</li>
              <li>Shape the dough</li>
              <li>Final proof (2-4 hours)</li>
              <li>Bake</li>
            </ol>
          </div>
          
          <div className="bg-amber-500/10 p-4 rounded-lg">
            <h4 className="text-amber-400 font-medium mb-2">Pro Tips</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Room temperature must be relatively stable—fluctuations can lead to unpredictable results</li>
              <li>Cooler rooms (65-70°F/18-21°C) work better for longer fermentations</li>
              <li>Reduce hydration slightly compared to direct method to prevent over-fermentation</li>
              <li>Cover dough well to prevent drying</li>
              <li>Judge readiness by dough appearance and feel, not just time</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">4. Cold Retard (Final Proof in Refrigerator)</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Overview</h4>
            <p className="text-sm text-white/80">
              Cold retard involves normal bulk fermentation at room temperature, followed by shaping and then refrigerating during the final proof. This method combines practical timing advantages with flavor benefits.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Characteristics</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Normal bulk fermentation, extended final proof</li>
                <li>Enhanced flavor development</li>
                <li>Improved crust development</li>
                <li>Easier scoring of cold dough</li>
                <li>Flexible baking schedule</li>
                <li>Can be combined with any of the previous methods</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Best For</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Baking first thing in the morning</li>
                <li>High-hydration doughs that are difficult to score</li>
                <li>Developing blisters on crust</li>
                <li>Controlling final proof timing</li>
                <li>Artisan breads with open crumb structure</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Process</h4>
            <ol className="list-decimal list-inside text-sm text-white/80 space-y-2">
              <li>Mix all ingredients</li>
              <li>Develop gluten structure</li>
              <li>Complete bulk fermentation at room temperature</li>
              <li>Shape the dough</li>
              <li>Place in banneton or proofing container</li>
              <li>Refrigerate for 8-24 hours</li>
              <li>Bake directly from refrigerator (no need to warm up)</li>
            </ol>
          </div>
          
          <div className="bg-amber-500/10 p-4 rounded-lg">
            <h4 className="text-amber-400 font-medium mb-2">Pro Tips</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Ensure bulk fermentation is properly completed before refrigerating</li>
              <li>Cold dough can be scored more precisely with less sticking</li>
              <li>Baking cold dough may require slightly longer baking time</li>
              <li>For maximum oven spring, preheat the oven thoroughly before baking cold dough</li>
              <li>Use rice flour in bannetons to prevent sticking with cold dough</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Comparing Fermentation Approaches</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/80">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4 text-amber-400">Approach</th>
                <th className="text-left py-3 px-4 text-amber-400">Total Time</th>
                <th className="text-left py-3 px-4 text-amber-400">Flavor Profile</th>
                <th className="text-left py-3 px-4 text-amber-400">Convenience</th>
                <th className="text-left py-3 px-4 text-amber-400">Skill Level</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Direct</td>
                <td className="py-3 px-4">4-8 hours</td>
                <td className="py-3 px-4">Basic, mild</td>
                <td className="py-3 px-4">High (same-day baking)</td>
                <td className="py-3 px-4">Beginner</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Cold Bulk</td>
                <td className="py-3 px-4">16-72 hours</td>
                <td className="py-3 px-4">Complex, tangy</td>
                <td className="py-3 px-4">Medium (requires planning)</td>
                <td className="py-3 px-4">Intermediate</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Slow Room Temp</td>
                <td className="py-3 px-4">12-24 hours</td>
                <td className="py-3 px-4">Rich, mild acidity</td>
                <td className="py-3 px-4">Medium (overnight)</td>
                <td className="py-3 px-4">Intermediate</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium">Cold Retard</td>
                <td className="py-3 px-4">12-30 hours</td>
                <td className="py-3 px-4">Complex, balanced</td>
                <td className="py-3 px-4">High (morning bake)</td>
                <td className="py-3 px-4">Beginner-Intermediate</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Choosing the Right Approach</h3>
        <p className="text-white/80 mb-4">
          Consider these factors when selecting a fermentation approach:
        </p>
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Time Availability</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li><span className="font-medium">Limited time in one block:</span> Direct fermentation</li>
              <li><span className="font-medium">Spread over multiple days:</span> Cold bulk or cold retard</li>
              <li><span className="font-medium">Overnight:</span> Slow room temperature or cold retard</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Desired Flavor Profile</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li><span className="font-medium">Mild, classic bread flavor:</span> Direct fermentation</li>
              <li><span className="font-medium">Complex, tangy flavor:</span> Cold bulk fermentation</li>
              <li><span className="font-medium">Balanced complexity:</span> Cold retard</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Dough Handling Preference</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li><span className="font-medium">Easier shaping:</span> Cold bulk (cold dough is less sticky)</li>
              <li><span className="font-medium">Easier scoring:</span> Cold retard (cold dough holds shape better)</li>
              <li><span className="font-medium">High hydration doughs:</span> Cold methods generally easier</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Practical Considerations</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li><span className="font-medium">Limited refrigerator space:</span> Direct or slow room temperature</li>
              <li><span className="font-medium">Morning baking:</span> Cold retard</li>
              <li><span className="font-medium">Unpredictable schedule:</span> Cold methods offer more flexibility</li>
            </ul>
          </div>
        </div>
        <p className="text-white/80 mt-4">
          Remember that these approaches can be combined and modified to suit your specific needs. Many bakers develop their own hybrid methods based on these fundamental approaches.
        </p>
      </div>
    </div>
  );
}